import React, {useState, useEffect} from "react";
import {useParams} from "react-router-dom";
import {Button, Code, Group, LoadingOverlay, Stack, Text} from "@mantine/core";
import {AlertCircle, Check} from "tabler-icons-react";
import {useModals} from "@mantine/modals";

import LoaderDefault from "../../components/common/Loader.default/Loader.default";
import AgentReviewTable from "../../components/project/Agent.review.table/Agent.review.table";
import AgentReview from "../../components/project/Agent.review/Agent.review";
import {IAgentDecision} from "../../api/Base/Base.types";
import {useGetAgentDataByID} from "../../api/AgentSignup/AgentSignup.hooks";
import AgentSignupApi from "../../api/AgentSignup/AgentSignup.api";
import AgentReviewDocuments from "../../components/project/Agent.review/Agent.review.documents";
import {IAgentSignupDocument} from "../../api/AgentSignup/AgentSignup.types";

function AgentReviewForm() {
    const {agentId} = useParams();
    const modals = useModals();

    const {isLoading, data} = useGetAgentDataByID(agentId!);
    const [documents, setDocuments] = useState<IAgentSignupDocument[]>([]);
    const [submittingForm, setSubmittingForm] = useState(false);

    useEffect(() => {
        if (data){
            getDocuments(data.email);
        }
    }, [data]);

    if (isLoading) {
        return <LoaderDefault text="Loading..."/>
    } else if (!data) {
        return <Text color="white">No Data Found.</Text>
    }

    async function getDocuments(email: string) {
        let documents = await AgentSignupApi.getDocuments({queryKey: [`loadAgentDocs-${email}`, {email}]});
        if (documents) setDocuments(documents);
    }

    async function onSubmitQuery(values: IAgentDecision) {
        if (data) {
            values.AgentId = agentId!;
            console.log("Agent ID: " + values.AgentId + "\nAgent Status: " + values.AgentStatus + "\nComments: " + values.Comments);
        } else {
            console.log("Agent ID not set.");
        }

        let res = await AgentSignupApi.postAgentReviewData({queryKey: ["postAgentReviewData", values]})
        setSubmittingForm(true);
        let commonModalProps = {
            centered: true,
            withCloseButton: false,
            closeOnClickOutside: false,
            closeOnEscape: false,
            readOnly: true,
        }
        if (res === true) {
            let id = modals.openModal({
                ...commonModalProps,
                title: (
                    <Group spacing="xs">
                        <Check color="#51cf66"/>
                        <Text>Agent Form Updated</Text>
                    </Group>
                ),
                children: (
                    <Stack>
                        <Text size="sm">
                            You have successfully updated the Agent's information.
                        </Text>
                        <Button mt={12} color="blue" onClick={() => {
                            modals.closeModal(id)
                        }}>
                            Close
                        </Button>
                    </Stack>
                ),
            })
            setSubmittingForm(false);
            return true;
        } else if (!res || res.errors) {
            modals.openModal({
                ...commonModalProps,
                title: (
                    <Group spacing="xs">
                        <AlertCircle color="#ff6b6b"/>
                        <Text>Error submitting Agent's form</Text>
                    </Group>
                ),
                children: (
                    <Stack>
                        <Text size="sm" align="left">
                            Your form <i>could not</i> be submitted due to: <br/>
                            <Text size="sm" color="red">
                                {res.errors.title || res.errors || "No Error Specified"}
                            </Text>
                        </Text>
                        <Text size="sm">
                            If you have gotten this error multiple times please contact support and provide them
                            with this error
                            ID: <br/><Code>{res.errors.traceId || "Agent-Create-No-Error-Code-Specified"}</Code>.
                        </Text>
                    </Stack>

                ),
            })
            setSubmittingForm(false);
            return false;
        }
    }

    return (
        <Stack style={{width: "100%", maxWidth: 1100}}>
            <LoadingOverlay visible={submittingForm}/>
            <Text size="xl" color="white">{data.agentName}</Text>
            <AgentReviewTable agentData={data}/>
            <Text size="lg" color="white">Uploaded Files</Text>
            <AgentReviewDocuments agentDocuments={documents}></AgentReviewDocuments>
            <AgentReview onSubmitQuery={onSubmitQuery} initialValues={data}/>
        </Stack>
    )
}

export default AgentReviewForm;
 